import { useEffect, useMemo, useRef } from "react";

/**
 * Adds a `.visible` class to the ref'd element when it scrolls into view.
 * Stable-by-default so re-renders of the calling component don't tear down
 * the observer.
 */
const useScrollAnimation = ({ threshold = 0.15, rootMargin = "0px" } = {}) => {
	const ref = useRef(null);
	const config = useMemo(
		() => ({ threshold, rootMargin }),
		[threshold, rootMargin]
	);

	useEffect(() => {
		const element = ref.current;
		if (!element) return undefined;

		// Respect prefers-reduced-motion: skip the observer and mark visible immediately.
		if (
			typeof window !== "undefined" &&
			window.matchMedia &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			element.classList.add("visible");
			return undefined;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					entry.target.classList.add("visible");
					observer.unobserve(entry.target);
				}
			},
			config
		);

		observer.observe(element);
		return () => observer.disconnect();
	}, [config]);

	return ref;
};

export default useScrollAnimation;
